import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Utils } from 'src/app/core/resources/utils';
import { Router } from '@angular/router';
import { Fmt } from 'src/app/core/messages/fmt';
import { Subscription } from 'rxjs';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { SatelliteLockType } from 'src/app/core/interfaces/satelliteLockType';
import { ServiceLock } from 'src/app/core/interfaces/satelliteLock';
import { MenuServiceService } from '../../service-request-form/menu-service/menu-service.service';
import { ServiceResources } from '../../service-request-form/resources/services';
import { CargoDetailService } from '../cargo-detail/cargo-detail.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';
import { DialogSatelliteLockComponent } from 'src/app/shared/dialog-satellite-lock/dialog-satellite-lock.component';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { AuthService } from 'src/app/core/services/authentication.service';
import { SatelliteLockStateDevice } from 'src/app/core/enums/satelliteLockStateDevice.enum';

@Component({
    selector: 'app-assign-satellite-lock',
    templateUrl: './assign-satellite-lock.component.html',
    styleUrls: ['./assign-satellite-lock.component.scss']
})
export class AssignSatelliteLockComponent implements OnInit, OnDestroy {
    permission = Permission;
    satelliteLockType = new FormControl('', Validators.required);
    satelliteLockControlSubscription: Subscription;
    satelliteLockForm: FormGroup;
    satelliteLockControl = new FormControl('', Validators.required);
    selectedSatelliteLock: SatelliteLock | null = null;
    optionsSatelliteLock: OptionsAutocomplete = {
        title: 'Candado satelital (ID o Consecutivo)',
        appearance: 'outline'
    };

    satelliteLockTypeId: string | null = null;
    satelliteLockTypeName: string = '';
    formValidate: string = '';

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public paramsDialog: { cargo: Cargo },
        public dialogRef: MatDialogRef<AssignSatelliteLockComponent>,
        private snackBarService: SnackBarService,
        private cargoDetailService: CargoDetailService,
        private spinner: NgxSpinnerService,
        public utils: Utils,
        private matDialog: MatDialog,
        private authService: AuthService
    ) {
        this.satelliteLockForm = new FormGroup({
            satelliteLock: this.satelliteLockControl
        });
    }

    async ngOnInit() {
        this.setLocalValues();
        this.initializeSatelliteLockForm();
        this.setupSatelliteLockControlSubscription();
    }

    private setupSatelliteLockControlSubscription() {
        this.satelliteLockControlSubscription = this.satelliteLockControl.valueChanges.subscribe(() => {
            this.onSatelliteLockSelected();
        });
    }

    private initializeSatelliteLockForm() {
        const lock: ServiceLock = this.utils.getNestedValue(this.paramsDialog.cargo, 'lock');
        if (lock && lock.id) {
            this.optionsSatelliteLock = {
                ...this.optionsSatelliteLock,
                initialValue: lock.id
            };
        }
    }


    private setLocalValues() {
        const lock: ServiceLock = this.utils.getNestedValue(this.paramsDialog.cargo, 'lock');
        let lockType = null;
        if (lock && lock.type && lock.type.id && lock.type.name) lockType = lock.type;
        if (lockType) {
            this.satelliteLockTypeId = lockType.id;
            this.satelliteLockTypeName = lockType.name;
            this.optionsSatelliteLock = {
                ...this.optionsSatelliteLock,
                filterByTypeName: lockType.name
            };
        }
    }


    onSatelliteLockSelected() {
        const selectedValue = this.satelliteLockControl.value;
        if (selectedValue && typeof selectedValue === 'object') this.selectedSatelliteLock = selectedValue as SatelliteLock;
        else this.selectedSatelliteLock = null;
    }


    private isValidForm(): boolean {
        if (this.utils.errorMessagesCustomized(this.satelliteLockControl, 'Candado satelital')) return false;
        if (!this.selectedSatelliteLock) {
            this.snackBarService.openSnackBar('Debe seleccionar un candado satelital', undefined, 'error');
            return false;
        }
        if ([SatelliteLockStateDevice.IN_MAINTENANCE, SatelliteLockStateDevice.INACTIVE].includes(this.selectedSatelliteLock.stateDevice)) {
            this.snackBarService.openSnackBar(`El candado satelital no está activo. Motivo: ${this.selectedSatelliteLock.description}`, undefined, 'error');
            return false;
        }
        return true;
    }

    private async assignLockToCargo(cargoId: string, lock: SatelliteLock): Promise<void> {
        const imei = lock.imei ? lock.imei.toString().trim() : undefined;
        return this.cargoDetailService.assignLock(cargoId, imei).toPromise();
    }


    async assignSatelliteLock() {
        if (!this.isValidForm()) return;
        const cargoId = this.paramsDialog.cargo.id;
        const lock = this.selectedSatelliteLock;
        const result = await this.openPreoperationalInspectionModal();
        if (!result) return;
        this.spinner.show();

        try {
            await this.assignLockToCargo(cargoId, lock);
            this.spinner.hide();
            this.snackBarService.openSnackBar("Candado asignado correctamente");
            this.dialogRef.close({ state: true });
        } catch (error) {
            console.error('Error al asignar candado:', error);
            this.spinner.hide();
            let errorMessage = 'Error en el servidor al asignar';
            if (error && error.error && typeof error.error === 'string') errorMessage = error.error;
            this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
        }
    }


    private async openPreoperationalInspectionModal(): Promise<boolean> {
        const dialogConfig = new MatDialogConfig();
        const cargo = this.paramsDialog.cargo;
        const userSession = this.authService.getUserSession();
        dialogConfig.data = {
            satelliteLock: this.selectedSatelliteLock,
            cargo: cargo,
            user: (userSession && userSession.information) || null,
            isPreoperationalInspection: true,
        };
        dialogConfig.width = ModalEnum.SMALL_WIDTH;
        dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
        dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
        dialogConfig.autoFocus = false;

        const result = await this.matDialog.open(DialogSatelliteLockComponent, dialogConfig).afterClosed().toPromise();
        return (result && result.state);
    }

    getStateDevice(): { text: string, class?: string } {
        if (!this.selectedSatelliteLock || !this.selectedSatelliteLock.stateDevice) return { text: '-' };
        if (this.selectedSatelliteLock.stateDevice === SatelliteLockStateDevice.ACTIVE) return { text: 'Activo' };
        if (this.selectedSatelliteLock.stateDevice === SatelliteLockStateDevice.INACTIVE) return { text: 'Inactivo', class: 'inactive' };
        if (this.selectedSatelliteLock.stateDevice === SatelliteLockStateDevice.IN_MAINTENANCE) return { text: 'En mantenimiento', class: 'inactive' };
        return { text: '-' };
    }

    ngOnDestroy() {
        if (this.satelliteLockControlSubscription) this.satelliteLockControlSubscription.unsubscribe();
    }
}
